/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  JobSpecTemplateRestService,
  SpecTemplateClauses,
  SpecTemplateParagraphIs,
  SpecTemplateSectionIs,
  SpecTemplateTextIs,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  formStateSnapshot,
  getRouteParam,
  getRouteQueryParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { map$ } from '@gh/rx/operators';
import { uniqueId } from 'lodash';
import { Observable } from 'rxjs';
import {
  JstClause,
  JstDetails,
  jstDetailsFromDto,
  jstDetailsToDto,
  JstParagraph,
  JstSection,
  newJstDetails,
  toClonedJstDetails,
} from './job-spec-template.model';

@Injectable()
export class JobSpecTemplateDetailsBusinessService implements InitViewService {
  constructor(private jobSpecTemplateRest: JobSpecTemplateRestService,
              private dictionaryService: DictionaryService,
              private translations: TranslationService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'jobSpecTemplateId');
    const sourceItemId = getRouteQueryParam(route, 'cloneFrom');

    return {
      details: isRouteForCreationMode(route)
        ? (sourceItemId ? this.clone(sourceItemId) : newJstDetails()) : this.get(itemId),
      paragraphIsDict: this.dictionaryService.getByEnum(SpecTemplateParagraphIs),
      sectionIsDict: this.dictionaryService.getByEnum(SpecTemplateSectionIs),
      textIsDict: this.dictionaryService.getByEnum(SpecTemplateTextIs),
      clausesAreDict: this.dictionaryService.getByEnum(SpecTemplateClauses),
    };
  }

  clone(id: ID): Observable<JstDetails> {
    return this.jobSpecTemplateRest.get(id).pipe(
      map$(jstDetailsFromDto),
      map$(toClonedJstDetails));
  }

  get(id: ID): Observable<JstDetails> {
    return this.jobSpecTemplateRest.get(id).pipe(map$(jstDetailsFromDto));
  }

  save(template: JstDetails): Observable<JstDetails> {
    if (template.specTemplateId) {
      return this.jobSpecTemplateRest.update(jstDetailsToDto(template)).pipe(map$(jstDetailsFromDto));
    } else {
      return this.jobSpecTemplateRest.create(jstDetailsToDto(template)).pipe(map$(jstDetailsFromDto));
    }
  }

  createNewParagraph(): JstParagraph {
    return {
      id: uniqueId('jst-paragraph:'),
      info: formStateSnapshot({
        name: this.translations.get('lbl_new_paragraph'),
        text: '',
        paragraphIs: SpecTemplateParagraphIs.Optional,
        textIs: SpecTemplateTextIs.Optional,
        isTextWarning: false,
      }),
      sections: [],
      isNew: true,
    };
  }

  createNewSection(): JstSection {
    return {
      id: uniqueId('jst-section:'),
      info: formStateSnapshot({
        name: this.translations.get('lbl_new_section'),
        text: '',
        sectionIs: SpecTemplateSectionIs.Optional,
        textIs: SpecTemplateTextIs.Optional,
        isTextWarning: false,
      }),
      clausesAre: SpecTemplateClauses.NotAllowed,
      clauses: [],
      isNew: true,
    };
  }

  createNewClause(): JstClause {
    return {
      id: uniqueId('jst-clause:'),
      text: this.translations.get('lbl_new_clause'),
      isTextWarning: false,
    };
  }
}
